import { type FC, type MouseEventHandler, type ReactNode, useState } from "react";
import { Button, Stack } from "~/common/mui";
import { CloseBtn } from "~/features/addToCart/components/AtcCloseButton";
import CircleSpinner from "~/common/components/CircleSpinner/CircleSpinner";
import { resolveCartPageUrl } from "~/utils/url/urlUtils";
import { useDispatch, useSelector } from "react-redux";
import { type RootState } from "~/app/store";
import {
  setAlreadyAddedToCart,
  updatePriceReference,
  updateQuantity,
} from "~/features/configurator/Linear/redux/actions";
import { userIsSalesman } from "~/domains/user/utils/roles";
import { LinearPopupState } from "~/features/configurator/Linear/constants";
import { getNetPrice, makeDescriptorFromFullState } from "~/domains/product/utils/productDescriptorUtils";
import { useLinearPrice } from "~/features/configurator/Linear/components/Substeps/useLinearPrice";
import { useTrackLCAddToCartClick } from "~/features/configurator/Linear/hooks/useTrackLCClicks";
import { selectedPriceReference } from "~/features/configurator/common/components/ConfigurationSummarySubstep";
import { useTranslation } from "react-i18next";
import { getConfiguratorDefinition } from "~/bridges/configurator/configuratorRegistry";
import { useLinearProductInfo } from "~/features/configurator/Linear/redux/useLinearProductInfo";
import { openModal } from "~/common/utils/ModalService";
import CartSuccessModal from "~/features/addToCart/components/AddToCartProcess/Modals/CartSuccessModal/CartSuccessModal";
import type { AddToCartResponseItem } from "~/features/cart/cart/api/AddToCart";
import type { NetPriceData } from "~/domains/pricing/types/PriceData";
import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";
import { ProductDescriptorContext } from "~/domains/product/ProductContext";
import {
  AtcDeliveryDisclaimer,
  AtcContentLayout,
  AtcMainAreaLayout,
  AtcPackagingLoading,
} from "~/features/addToCart/components/AddToCartProcess/shell/AtcContentLayout";
import { AtcDialogShell } from "~/features/addToCart/components/AddToCartProcess/shell/AtcDialogShell";
import { AtcProductDetails } from "~/features/addToCart/components/AddToCartProcess/shell/AtcProductDetails";
import { INVALID_PACKAGE_CODE } from "~/utils/constants";
import { PriceBlock } from "~/features/productDetails/components/ProductSummary/components/PdpPriceBlock";
import { QuantityInput } from "~/common/components/QuantityInput/QuantityInput";
import { PriceReferenceParam } from "~/features/cart/cart/components/LineItemEditableParams";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";

interface Props {
  onClose: () => void;
}

/**
 * Temporary bridge while the configurator /addToCart2 endpoint returns only the new configuration id.
 * This is a modal-only fake response so CartSuccessModal can move toward response-based rendering without
 * changing the backend contract in this pass.
 */
function fakeResponseFromLinearConfigurator({
  product,
  quantity,
  packCode,
  price,
  priceReference,
  newConfigId,
}: {
  product: ProductDescriptor;
  quantity: number;
  packCode: string;
  price?: NetPriceData;
  priceReference: string;
  newConfigId?: string;
}): AddToCartResponseItem {
  return {
    pk: Number.parseInt(newConfigId ?? "", 10) || 0,
    sapMaterialNumber: product.base.sapMaterialNumber,
    designation: product.base.designation,
    productCode: product.base.code,
    packCode,
    packSize: 1,
    quantity,
    actualNetPrice: price ? { currencyIso: price.currencyIso, value: price.value } : undefined,
    priceUnit: price?.unit,
    priceReference,
  };
}

const LinearATCPopup: FC<Props> = (props: Props) => {
  const { onClose } = props;
  const { t } = useTranslation();

  const dispatch = useDispatch();

  const [state, setState] = useState(LinearPopupState.INITIAL);

  const linearState = useSelector((state: RootState) => state.linearConfigurator);

  const productInfo = useLinearProductInfo();

  const quantity = useSelector(getQuantity);

  const trackClick = useTrackLCAddToCartClick();

  const priceReference = useSelector(selectedPriceReference);
  const packCode = INVALID_PACKAGE_CODE;

  const { priceInfo, isLoadingPrice } = useLinearPrice({
    priceReference: priceReference,
  });
  const productDescriptor = makeDescriptorFromFullState(
    productInfo,
    { state: isLoadingPrice ? "loading" : "loaded", data: priceInfo ? [priceInfo] : [] },
    undefined,
    priceReference,
  );
  const price = getNetPrice({
    totalQuantity: quantity,
    packCode: "",
    product: productDescriptor,
  });

  const isInState = (...validStates: LinearPopupState[]) => {
    const result = validStates.find((el) => el === state);
    return result !== undefined;
  };

  const onAddToCart = async () => {
    setState(LinearPopupState.SENDING);
    let success: boolean;
    let newConfigId: string | undefined = undefined;
    try {
      const productCode = linearState.productCode;
      const addConfiguredProductToCart = getConfiguratorDefinition(productCode).apiCalls.addToCart;

      newConfigId = await addConfiguredProductToCart(linearState, priceInfo);
      success = true;
      window.SCHA.updateMinicart?.();
      trackClick();
    } catch (e) {
      console.error(e);
      success = false;
    }
    if (success) {
      dispatch(setAlreadyAddedToCart(newConfigId));
      onClose();
      openModal("add-to-cart-dialog", (close) => (
        <CartSuccessModal
          linearConfig={productDescriptor.base}
          onClose={close}
          page="LC"
          responseItems={[
            fakeResponseFromLinearConfigurator({
              product: productDescriptor,
              quantity,
              packCode,
              price,
              priceReference,
              newConfigId,
            }),
          ]}
        />
      ));
    } else {
      setState(LinearPopupState.ERROR);
    }
  };

  const finalState = isInState(LinearPopupState.SUCCESS, LinearPopupState.ERROR);
  const primaryButtonLabel = t(
    `page.linear.addToCart.button.${finalState ? "go" : "add"}To${userIsSalesman() ? "Quote" : "Cart"}`,
  );
  const primaryButtonUrl = finalState ? resolveCartPageUrl() : undefined;

  const footer = (
    <>
      {isInState(LinearPopupState.INITIAL, LinearPopupState.SENDING) ? (
        <CloseBtn onClick={onClose} disabled={isInState(LinearPopupState.SENDING)}>
          {t("cancel")}
        </CloseBtn>
      ) : (
        <CloseBtn onClick={onClose}>{t("close")}</CloseBtn>
      )}

      <AddToCartButton
        disabled={isInState(LinearPopupState.SENDING, LinearPopupState.ERROR)}
        onClick={isInState(LinearPopupState.INITIAL) ? onAddToCart : undefined}
        href={primaryButtonUrl}
      >
        {primaryButtonLabel}
      </AddToCartButton>
    </>
  );

  const packagingSlot = (
    <>
      {isInState(LinearPopupState.INITIAL) && <p>{t("page.linear.addToCart.text.initial")}</p>}
      {isInState(LinearPopupState.SENDING) && (
        <AtcPackagingLoading className="scha__atc-popup--column-packaging-loading">
          <CircleSpinner />
        </AtcPackagingLoading>
      )}
      {isInState(LinearPopupState.ERROR) && (
        <AtcDeliveryDisclaimer severity="error" className="scha-atc__delivery-disclaimer no-bottom">
          {t("page.linear.addToCart.text.error")}
        </AtcDeliveryDisclaimer>
      )}
    </>
  );

  const pricingSlot = (
    <Stack gap={2}>
      <LinearATCPriceBlock
        netPrice={price}
        descriptor={productDescriptor}
        packCode={packCode}
        priceReference={priceReference}
        quantity={quantity}
      />
      <PriceReferenceParam
        priceReference={priceReference}
        externalLabel={true}
        product={productDescriptor.base}
        editable={true}
        onChange={(newValue: string | undefined) => dispatch(updatePriceReference(newValue))}
      />
      <QuantityInput
        variant="compact"
        onValueChange={(newQuantity: number) => dispatch(updateQuantity(newQuantity))}
        value={quantity}
        step={1}
      />
    </Stack>
  );
  const content = <AtcMainAreaLayout packagingSlot={packagingSlot} pricingSlot={pricingSlot} />;

  return (
    <ProductDescriptorContext value={productDescriptor}>
      <AtcDialogShell title={t("addToCartPopup.title")} onClose={onClose} footer={footer}>
        <AtcContentLayout
          info={<AtcProductDetails descriptor={productDescriptor} packCode={packCode} />}
          main={content}
        />
      </AtcDialogShell>
    </ProductDescriptorContext>
  );
};

const getQuantity = (state: RootState) => state.linearConfigurator.quantity;

export default LinearATCPopup;

interface ButtonProps {
  className?: string;
  onClick?: MouseEventHandler<HTMLElement>;
  href?: string;
  disabled?: boolean;
  children?: ReactNode;
}

const AddToCartButton: FC<ButtonProps> = (props) => (
  <Button
    startIcon={<ShoppingCartIcon sx={{ fontSize: { phone: 16, tablet: 20 } }} />}
    disabled={window.SCHA.config.disableAllWS || (props.disabled == null ? false : props.disabled)}
    {...props}
  />
);

interface LinearATCPriceBlockProps {
  descriptor: ProductDescriptor;
  netPrice: NetPriceData;
  packCode: string;
  priceReference: string;
  quantity: number;
}

const LinearATCPriceBlock = (props: LinearATCPriceBlockProps) => {
  return <PriceBlock {...props} selectedParts={[]} salePriceEnabled={false} />;
};
